/**
 * Side-KATALOG + adgangstjek ([D-141]: per-bruger, per-side grants).
 *
 * Kataloget (titel/sektion/sortering pr. side) hentes fortsat fra
 * GET /api/settings/permissions ved app-start — men adgangs-semantikken bor
 * der IKKE længere: brugerens grants leveres af /api/auth/me
 * (auth.user.grants), og canAccessPath/canEditPath slår op i dem.
 * INGEN grant = INGEN adgang (fail-closed — også for sider der tilføjes
 * senere). super_admin bypasser alt. Ruter UDEN katalog-side (fx /login)
 * er fortsat åbne (fail-open for ikke-app-ruter — uændret princip).
 *
 * To undtagelser fra grant-opslaget ([D-141]-review-fixes):
 *   - Rolle-gatede sider (ROLE_GATED_PAGE_PATHS): serveren gater dem på
 *     requireRoles(['super_admin']) ([D-220]), ikke på grants — så UI'et skal
 *     også bruge rollen, ellers låses en super_admin ude af en side serveren
 *     tillader.
 *   - Fallback-katalog: fejler katalog-hentningen, resolves der mod brugerens
 *     grant-nøgler frem for at fail-open til fri adgang.
 */

import { reactive } from 'vue';
import { presentationAccess } from '@cockpit/shared';

import type { RoleCode, RoutePermission } from '../permissions';
import { auth } from './useAuth';

export interface PermissionsState {
  loaded: boolean;
  /** Side-kataloget (metadata pr. side) — IKKE adgangsregler ([D-141]). */
  pages: RoutePermission[];
}

export const permissionsState = reactive<PermissionsState>({
  loaded: false,
  pages: [],
});

/**
 * Katalog-sider der er ROLLE-gatede server-side (requireRoles admin/super_admin
 * via api-authz' SKIP_PREFIXES) — IKKE grant-gatede ([D-141]-review-fix
 * #7/#17/#18). Adgangen afgøres af rollen både i UI og på serveren; et
 * per-bruger grant på dem er en no-op, så editoren (UserDetailView) skjuler dem,
 * og canAccessPath/canEditPath bruger rollen frem for grant-opslag.
 * NB: /settings/api-keys hører IKKE til her — dens data-API (/api/integrations/*)
 * ER grant-gated, så grant-semantikken er korrekt for den.
 */
export const ROLE_GATED_PAGE_PATHS: ReadonlySet<string> = new Set([
  '/settings/users',
  // Siderettigheder ([D-178]): side-centreret grant-administration — API'et er
  // requireRoles admin/super_admin ligesom Brugere, så grants er no-ops her.
  '/settings/page-permissions',
]);

/** [D-220]: de rolle-gatede sider (Brugere, Siderettigheder) er super_admin-
 *  only. Funktionen består som ét sted at læse reglen, og serveren håndhæver
 *  den samme med requireRoles(['super_admin']). */
function isSuperAdmin(roles: RoleCode[]): boolean {
  return roles.includes('super_admin');
}

export async function fetchPermissions(): Promise<void> {
  try {
    const res = await fetch('/api/settings/permissions', { credentials: 'same-origin' });
    if (res.ok) {
      const data = (await res.json()) as { pages: RoutePermission[] };
      permissionsState.pages = data.pages;
    }
  } catch {
    // Håndteret i catalogPages(): fejler hentningen, resolves der mod brugerens
    // grant-nøgler (fail-closed) frem for at fail-open til fri adgang. Grants
    // håndhæves desuden altid af serveren (api-authz).
  } finally {
    permissionsState.loaded = true;
  }
}

/**
 * Det katalog opslagene skal ske mod. Normalt side-kataloget fra serveren; er
 * hentningen fejlet (loaded, men tomt), bygges et erstatnings-katalog af
 * brugerens grant-nøgler (nøgle == side-sti) så adgang stadig KRÆVER et grant
 * ([D-141]-review-fix #20 — ellers fail-open til true resten af sessionen).
 * `fallback` fortæller canAccessPath at en "ukendt rute" i denne tilstand skal
 * lukkes, ikke åbnes.
 */
export function catalogPages(): { pages: RoutePermission[]; fallback: boolean } {
  if (permissionsState.pages.length > 0) {
    return { pages: permissionsState.pages, fallback: false };
  }
  if (permissionsState.loaded) {
    const keys = Object.keys(auth.user?.grants ?? {});
    const pages: RoutePermission[] = keys.map((path) => ({
      path,
      title: path,
      section: '',
      sortOrder: 0,
    }));
    return { pages, fallback: true };
  }
  return { pages: [], fallback: false };
}

/** Finder katalog-siden for `path` i `pages` — eksakt match, ellers længste prefix. */
function resolveIn(pages: RoutePermission[], path: string): RoutePermission | undefined {
  if (!pages.length) return undefined;
  const exact = pages.find((p) => p.path === path);
  if (exact) return exact;
  return pages
    .filter((p) => path.startsWith(p.path + '/'))
    .sort((a, b) => b.path.length - a.path.length)[0];
}

/**
 * Må brugeren med `roles` SE `path`? super_admin altid. Rolle-gatede
 * katalog-sider (ROLE_GATED_PAGE_PATHS) afgøres af rollen. En rute uden
 * katalog-side er åben (ikke-app-ruter, fx /login) — MEN i fallback-tilstand
 * (katalog-hentning fejlet) lukkes ukendte ruter, så adgang kræver et grant.
 * Ellers kræves et grant på siden ([D-141], fail-closed).
 */
export function canAccessPath(path: string, roles: RoleCode[]): boolean {
  if (roles.includes('super_admin')) return true;
  // [D-534]: præsentationsgrenen arver nedad (Alle, type, dæk; det højeste vinder),
  // og oversigten og typen kan ses med et grant længere nede (filtreret liste).
  const branch = presentationAccess(path, (p) => auth.user?.grants?.[p]);
  if (branch !== null) return branch != null;
  const { pages, fallback } = catalogPages();
  const page = resolveIn(pages, path);
  if (!page) return !fallback; // normal: åben ikke-app-rute · fallback: luk
  if (ROLE_GATED_PAGE_PATHS.has(page.path)) return isSuperAdmin(roles);
  return auth.user?.grants?.[page.path] != null;
}

/**
 * Må brugeren med `roles` REDIGERE på `path`? super_admin altid. Rolle-gatede
 * sider afgøres af rollen; en rute uden katalog-side → false (fail-closed);
 * ellers kræves brugerens EGET 'edit'-grant ([D-141]) — samme semantik som
 * serverens api-authz.
 */
export function canEditPath(path: string, roles: RoleCode[]): boolean {
  if (roles.includes('super_admin')) return true;
  const branch = presentationAccess(path, (p) => auth.user?.grants?.[p]);
  if (branch !== null) return branch === 'edit';
  const { pages } = catalogPages();
  const page = resolveIn(pages, path);
  if (!page) return false;
  if (ROLE_GATED_PAGE_PATHS.has(page.path)) return isSuperAdmin(roles);
  return auth.user?.grants?.[page.path] === 'edit';
}

/**
 * Første side brugeren MÅ se — til router-redirect ved afvist adgang og til
 * login-landing ([D-141]-review-fix #8/#15, loop-værn). super_admin →
 * '/marketing/blended' (sektionens tværgående side, [D-198]); ellers den første
 * katalog-side (sorteret på sortOrder) hvor canAccessPath er sand; ingen
 * tilladt side → null (kalderen sender til /no-access). Afgøres via
 * canAccessPath, så resultatet ALDRIG er en sti router-guarden selv ville
 * afvise (ellers reintroduceres loopet).
 */
export function firstAllowedPath(roles: RoleCode[]): string | null {
  if (roles.includes('super_admin')) return '/marketing/blended';
  const { pages } = catalogPages();
  const sorted = [...pages].sort((a, b) => a.sortOrder - b.sortOrder);
  for (const p of sorted) {
    if (canAccessPath(p.path, roles)) return p.path;
  }
  return null;
}
